import React, { useCallback, useMemo } from 'react';
import { GestureResponderEvent, Pressable, ScrollView, Text, View } from 'react-native';
import type { DateTime } from 'luxon';
import { calendarColors, calendarStyles } from './calendar.styles';
import { WEEKDAY_LABELS, WEEK_HOURS, clampHour, isSameDay, weekDays } from './calendar.dates';
import { EventPill } from './EventPill';
import { useCalendarDrag, weekTargetKey } from './calendar.drag';
import type { CalendarEvent } from './calendar.types';

/**
 * On RN-Web a `View`/`Pressable` ref IS the DOM node, so it exposes
 * `getBoundingClientRect`. Read live at hit-test time (never cached) so the rect
 * reflects current transform/scroll (gripp-uzs.15 fix).
 */
type DomViewNode = { getBoundingClientRect?: () => DOMRect };

export interface WeekGridProps {
  /** Anchor day inside the week to render (any day of that week). */
  anchor: DateTime;
  /** "Today" for the today-column header + slot tint. */
  today: DateTime;
  /** All events for the visible window; bucketed per day+hour internally. */
  events: CalendarEvent[];
  /** Currently-open activity id (highlighted pill). */
  selectedId?: string | null;
  /** Show the asset second line inside each pill (§1 `showAssetInPill`). */
  showAsset?: boolean;
  /** Open an activity (consumer wires `/activity/:id`). */
  onOpenEvent?: (id: string) => void;
  /**
   * Drag-over hook for Phase 3 — the `${colIndex}-${hour}` slot key currently
   * under a drag, rendered with the accent + inset ring. No drag *logic* here yet
   * (mirrors MonthGrid's hook; P3 wires the DnD that drives it + a drop callback).
   */
  dragOverSlot?: string | null;
  /**
   * Slot press passthrough for Phase 3 drop wiring. Receives the slot's day and
   * hour. Foundation/mock usage can ignore it.
   */
  onSlotPress?: (day: DateTime, hour: number) => void;
}

/** Slot key matching `dragOverSlot` / drop targets: "<colIndex>-<hour>". */
export const weekSlotKey = (colIndex: number, hour: number): string => `${colIndex}-${hour}`;

/**
 * Week layout — exact hourly time-grid (plan 0017 §1 "Week grid"). 7 day-columns ×
 * 13 hourly rows (6am–6pm), built with flexbox (RN-Web has no CSS grid). The header
 * row is kept outside the ScrollView so it stays fixed while the slots scroll
 * (RN-Web `position: sticky` is unreliable; a fixed header is the faithful
 * equivalent of gripp-OS's `sticky top-0`). All events shown — no "+N more".
 */
export const WeekGrid: React.FC<WeekGridProps> = ({
  anchor,
  today,
  events,
  selectedId,
  showAsset = false,
  onOpenEvent,
  dragOverSlot,
  onSlotPress,
}) => {
  const days = useMemo(() => weekDays(anchor), [anchor]);
  const drag = useCalendarDrag();

  // Register/unregister a slot's drop target via its DOM node. getRect() reads
  // getBoundingClientRect LIVE at hit-test time — survives scroll + transforms.
  const slotRef = useCallback(
    (node: DomViewNode | null, colIndex: number, hour: number, day: DateTime) => {
      if (!drag?.enabled) return;
      const key = weekTargetKey(colIndex, hour);
      if (!node) {
        drag.unregisterTarget(key);
        return;
      }
      drag.registerTarget(key, {
        hour,
        day,
        getRect: () => {
          const r = node.getBoundingClientRect?.();
          return r ? { left: r.left, top: r.top, right: r.right, bottom: r.bottom } : null;
        },
      });
    },
    [drag],
  );

  // Bucket events by "<dayKey>-<clampedHour>" once; slots read their slice.
  const eventsBySlot = useMemo(() => {
    const map = new Map<string, CalendarEvent[]>();
    for (const ev of events) {
      const key = `${ev.at.toFormat('yyyy-LL-dd')}-${clampHour(ev.hour)}`;
      const arr = map.get(key);
      if (arr) arr.push(ev);
      else map.set(key, [ev]);
    }
    return map;
  }, [events]);

  return (
    // `gridOuterFill` (flex: 1) bounds the grid to the column height so the inner
    // ScrollView has a height to scroll within — without it the grid sizes to
    // content and a busy week overflows the column with no scrollbar.
    <View style={[calendarStyles.gridOuter, calendarStyles.gridOuterFill]}>
      {/* Fixed header row — day-of-week + date per column */}
      <View style={calendarStyles.gridRow}>
        {days.map((day, colIndex) => {
          const isToday = isSameDay(day, today);
          return (
            <View
              key={colIndex}
              style={[
                calendarStyles.gridCol,
                calendarStyles.weekHeaderCell,
                colIndex > 0 && calendarStyles.cellLeftBorder,
                isToday && calendarStyles.cellTodayBg, // full slate-200 (§1)
              ]}
            >
              <Text
                style={[
                  calendarStyles.weekHeaderText,
                  isToday && {
                    color: calendarColors.weekTodayHeaderText, // brand (#FF5505)
                    fontWeight: '600',
                  },
                ]}
              >
                {WEEKDAY_LABELS[day.weekday % 7]}
              </Text>
              <Text
                style={[
                  calendarStyles.weekHeaderText,
                  isToday && {
                    color: calendarColors.weekTodayHeaderText,
                    fontWeight: '600',
                  },
                ]}
              >
                {day.day}
              </Text>
            </View>
          );
        })}
      </View>

      {/* Scrollable hour rows — flex:1 so it fills the height left under the
          fixed header and scrolls when the hours exceed the column. */}
      <ScrollView style={calendarStyles.gridOuterFill}>
        {WEEK_HOURS.map((hour) => (
          <View key={hour} style={calendarStyles.gridRow}>
            {days.map((day, colIndex) => {
              const isToday = isSameDay(day, today);
              const slotKey = weekSlotKey(colIndex, hour);
              // Highlight from the live drag controller OR the explicit prop.
              const isDragOver =
                dragOverSlot === slotKey ||
                drag?.activeDropKey === weekTargetKey(colIndex, hour);
              const slotEvents =
                eventsBySlot.get(`${day.toFormat('yyyy-LL-dd')}-${hour}`) ?? [];

              const handlePress = (e: GestureResponderEvent) => {
                e.stopPropagation();
                onSlotPress?.(day, hour);
              };

              return (
                <Pressable
                  key={colIndex}
                  ref={(node) => slotRef(node as DomViewNode | null, colIndex, hour, day)}
                  onPress={onSlotPress ? handlePress : undefined}
                  style={[
                    calendarStyles.weekHourSlot,
                    colIndex > 0 && calendarStyles.cellLeftBorder,
                    // Background precedence: drag-over > today (§1), matching MonthGrid.
                    isDragOver
                      ? [
                          calendarStyles.cellDragOverRing,
                          { borderColor: calendarColors.dragOverRing },
                        ]
                      : isToday
                      ? { backgroundColor: calendarColors.todayCellTint }
                      : undefined,
                  ]}
                >
                  {slotEvents.map((ev) => (
                    <EventPill
                      key={ev.id}
                      event={ev}
                      selected={selectedId === ev.id}
                      dense
                      showAsset={showAsset}
                      onPress={onOpenEvent}
                    />
                  ))}
                </Pressable>
              );
            })}
          </View>
        ))}
      </ScrollView>
    </View>
  );
};

WeekGrid.displayName = 'WeekGrid';
